import { CSSProperties, memo, ReactNode, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@clerk/react";
import { format } from "date-fns";
import {
  AlertTriangleIcon,
  AlertCircleIcon,
  BanknoteIcon,
  BellOffIcon,
  CircleChevronRightIcon,
  ChevronRightIcon,
  CircleDollarSignIcon,
  ClipboardCheckIcon,
  ClockIcon,
  FilePenLineIcon,
  HourglassIcon,
  MailQuestionIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { statusAccentStyle } from "@/constants/statuses";
import WaitingApprovalAction from "@/components/dashboard_campaigns/waiting-approval-action";
import { StatusColumnCellProps } from "../types";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import { useCampaignStore, useCreatorsStore } from "@/stores";
import {
  evePayoutState,
  evePhaseKeyForStatus,
  isEveCampaign,
  isEvePayoutSlotKey,
  pipelineForCreator,
  resolveEvePhaseSettings,
} from "@/lib/eve";
import { payoutPrepared } from "../phase/phaseState";
import { phaseDisplayLabel } from "../phase/blocks";

function StatusColumnCell({
  creator,
  status,
  loadingThisCreator,
  campaign,
  onRefresh,
}: StatusColumnCellProps) {
  const { getToken } = useAuth();
  const { advanceCreatorStage } = useCampaignStore();
  const { patchCreator } = useCreatorsStore();
  // The row's own campaign (prop) — org-wide rows span campaigns, so the
  // campaign store's "open campaign" is never consulted here.
  const campaignId = campaign?._id ?? null;
  const { openTask: openTaskFor } = useCreatorsTableContext();
  const [isAdvancing, setIsAdvancing] = useState(false);

  const creatorHasHandOff = creator.hand_off_to_user || false;
  const creatorUnansweredQuestion = creator.unanswered_question || false;
  const creatorHasError = creator.error || "";

  const approvalDateLabel = creator.waiting_approval_date
    ? `Ready for approval since ${format(
        new Date(creator.waiting_approval_date),
        "MMM d, yyyy",
      )}`
    : undefined;

  const isEve = isEveCampaign(campaign);
  // Eve campaigns never run legacy stages — suppressing them here disables
  // the whole manual-advance/delay machinery below in one move.
  const stages = isEve ? undefined : campaign?.stages;
  const currentStage = stages?.find((s) => s.id === status);
  const isCustomStatus = !!currentStage;
  const sortedStages = stages
    ? [...stages].sort((a, b) => a.order - b.order)
    : [];
  const currentStageIndex = sortedStages.findIndex((s) => s.id === status);
  const nextStage =
    currentStageIndex >= 0 ? sortedStages[currentStageIndex + 1] : null;
  const stageInfo = creator.stage_info;
  const isSending = stageInfo?.status === "sending";
  const isPendingOrSending =
    stageInfo?.status === "pending" || isSending;
  const isFirstStagePending =
    isCustomStatus && currentStageIndex === 0 && isPendingOrSending;
  const isManualPending =
    isCustomStatus &&
    currentStage?.trigger === "manual" &&
    isPendingOrSending &&
    !isFirstStagePending;

  const delayCountdown = (() => {
    if (
      !isCustomStatus ||
      stageInfo?.status !== "pending" ||
      currentStage?.trigger !== "time_delay" ||
      !currentStage?.delay_seconds
    )
      return null;

    let baseTimestamp: number;

    if (currentStage.trigger_field) {
      const dateFieldValue = creator.fields?.find(
        (f) => f.name === currentStage.trigger_field,
      )?.value;
      if (!dateFieldValue) return "waiting for date";
      const parsed = new Date(dateFieldValue);
      if (isNaN(parsed.getTime())) return "sending soon";
      baseTimestamp = parsed.getTime() / 1000;
    } else {
      if (!stageInfo?.entered_at) return null;
      baseTimestamp = new Date(stageInfo.entered_at).getTime() / 1000;
    }

    if (isNaN(baseTimestamp)) return "sending soon";

    const targetTimestamp = currentStage.trigger_field_before
      ? baseTimestamp - currentStage.delay_seconds
      : baseTimestamp + currentStage.delay_seconds;

    const remainingSec = targetTimestamp - Date.now() / 1000;

    if (remainingSec <= 0) return "sending soon";
    if (remainingSec >= 86400) return `in ${Math.ceil(remainingSec / 86400)}d`;
    return `in ${Math.ceil(remainingSec / 3600)}h`;
  })();

  async function handleAdvance() {
    if (isAdvancing) return;
    const token = await getToken();
    if (!token || !campaignId) return;
    setIsAdvancing(true);

    // Optimistically patch just this row so the table doesn't flash a full
    // refetch. Mirrors the backend's two branches in advance_creator_stage:
    // a pending stage with a template → email is queued + status "sending";
    // otherwise the creator advances synchronously to the next stage.
    const baseStageInfo = stageInfo ?? {};
    const optimistic: Partial<typeof creator> =
      currentStage?.template && stageInfo?.status === "pending"
        ? { stage_info: { ...baseStageInfo, status: "sending" } }
        : nextStage
        ? {
            status: nextStage.id,
            stage_info: {
              ...baseStageInfo,
              current_stage_id: nextStage.id,
              status: "pending",
            },
          }
        : {
            status: "completed",
            stage_info: {
              ...baseStageInfo,
              current_stage_id: null,
              status: "completed",
            },
          };
    patchCreator(creator._id, optimistic);

    try {
      const success = await advanceCreatorStage(creator._id, token);
      if (!success) {
        // Reconcile with server truth only on failure.
        await onRefresh();
      }
    } finally {
      setIsAdvancing(false);
    }
  }

  // A "needs you" state's icon: tooltip says what is parked, clicking lands
  // on this creator's Tasks action (TasksDashboard reads ?action=<id>; the
  // task detail carries the real confirm/review/release controls).
  const openTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    openTaskFor(creator._id);
  };

  const taskIcon = (key: string, icon: ReactNode, className: string, tip: string, style?: CSSProperties) => (
    <Tooltip delayDuration={0} key={key}>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          className={`size-7 [&_svg]:size-3.5 ${className}`}
          style={style}
          aria-label={tip}
          onClick={openTask}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-72 text-center">{tip}</p>
      </TooltipContent>
    </Tooltip>
  );

  // Amy-v2 states parked on a human, one icon each — mirrors the Tasks
  // builders (_get_eve_tasks): staged payout, parked review verdict
  // (approval mode always; assist/auto only once Amy parks a
  // recommendation — a mid-judge icon would mislead), parked draft,
  // stalled wait-hold, exhausted nudges.
  // The pipeline THIS creator runs — their closing tier's (entries, labels,
  // nudge caps and payout ordinals are per tier); [] on legacy campaigns.
  const pipeline = pipelineForCreator(campaign, creator);
  const attentionIcons: ReactNode[] = [];
  // A pending contract change request (campaign_creators.eve_contract_change)
  // — the Tasks page's "Contract change request" row, painted in that status
  // item's color. First, like the Tasks builder ranks it.
  if (creator.eve_contract_change?.status === "pending") {
    const ask = creator.eve_contract_change.ask_summary;
    attentionIcons.push(
      taskIcon(
        "contract-change",
        <FilePenLineIcon />,
        "bg-(--status-accent)/10 text-(--status-accent) hover:bg-(--status-accent)/20 hover:text-(--status-accent)",
        `Asked to change the contract${ask ? `: ${ask}` : ""} — approve or decline Amy's proposed wording in the task`,
        statusAccentStyle("contract-change")
      )
    );
  }
  // Amy's offer task (campaign_creators.eve_offer_request): she sends this
  // creator nothing until the team sets the tier's offer on the Tasks page.
  if (creator.eve_offer_request?.tier_order != null) {
    attentionIcons.push(
      taskIcon(
        "offer-review",
        <CircleDollarSignIcon />,
        "bg-(--status-accent)/10 text-(--status-accent) hover:bg-(--status-accent)/20 hover:text-(--status-accent)",
        "Amy is waiting for this creator's offer. Open the task.",
        statusAccentStyle("offer-review")
      )
    );
  }
  if (isEveCampaign(campaign)) {
    const phaseKey = evePhaseKeyForStatus(status);
    const phaseEntry = pipeline.find((p) => p.key === phaseKey);
    const phaseLabel = phaseKey ? phaseDisplayLabel(pipeline, phaseKey) : "";

    if (phaseKey && isEvePayoutSlotKey(phaseKey) && status.startsWith("pending-")) {
      const pay = evePayoutState(creator.eve_phase, phaseKey);
      if (payoutPrepared(pay)) {
        attentionIcons.push(
          taskIcon(
            "payout",
            <BanknoteIcon />,
            "bg-success/10 text-success hover:bg-success/20 hover:text-success",
            `${phaseLabel} payout of ${pay?.preview?.amount} ${pay?.preview?.currency ?? "USD"} is waiting for your confirmation — open the task`
          )
        );
      }
    }

    if (phaseKey && status.endsWith("-review")) {
      const judgeMode = phaseEntry ? resolveEvePhaseSettings(phaseEntry).judgeMode : "approval";
      const kindArtifacts = (creator.eve_phase?.artifacts ?? []).filter((a) => a.kind === phaseKey);
      const rec = kindArtifacts[kindArtifacts.length - 1]?.recommendation;
      if (judgeMode === "approval" || rec) {
        attentionIcons.push(
          taskIcon(
            "review",
            <ClipboardCheckIcon />,
            "bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500/20 hover:text-indigo-500",
            rec
              ? `Amy recommends ${rec.verdict === "approve" ? "approving" : "escalating"} this ${phaseLabel} submission — the verdict is yours`
              : `${phaseLabel} submission is waiting for your review — open the task`
          )
        );
      }
    }

    if (creator.next_message) {
      attentionIcons.push(
        taskIcon(
          "draft",
          <MailQuestionIcon />,
          "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 hover:text-blue-500",
          "A drafted email is waiting for your approval — open the task"
        )
      );
    }

    if (creator.eve_phase?.gate?.stall_announced_at) {
      attentionIcons.push(
        taskIcon(
          "stall",
          <HourglassIcon />,
          "bg-warning/10 text-warning hover:bg-warning/20 hover:text-warning",
          `The ${phaseLabel || "wait"} hold has stalled — Amy flagged it for you`
        )
      );
    }

    if (status.startsWith("waiting-") && phaseEntry) {
      const cap = resolveEvePhaseSettings(phaseEntry).maxNudges;
      if (cap > 0 && (creator.eve_phase?.nudge_count ?? 0) >= cap) {
        attentionIcons.push(
          taskIcon(
            "nudges",
            <BellOffIcon />,
            "bg-warning/10 text-warning hover:bg-warning/20 hover:text-warning",
            `${phaseLabel}: all ${cap} nudges sent and the creator stayed quiet — Amy flagged it for you`
          )
        );
      }
    }
  }

  const warningIcon = creatorHasHandOff && (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          variant="warning-ghost"
          className="size-7 [&_svg]:size-3.5 bg-warning/10"
          aria-label="Open the handoff task"
          onClick={openTask}
        >
          <AlertTriangleIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-72 text-center">
          {creatorUnansweredQuestion || "Handed off to you — open the task"}
        </p>
      </TooltipContent>
    </Tooltip>
  );

  const errorIcon = creatorHasError && (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          variant="destructive-ghost"
          className="size-7 [&_svg]:size-3.5 cursor-default bg-destructive/10"
          scope={false}
        >
          <AlertCircleIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-72 text-center">{creatorHasError}</p>
      </TooltipContent>
    </Tooltip>
  );

  if (status === "waiting-approval") {
    return (
      <div className="flex items-center gap-2">
        <WaitingApprovalAction
          status={status}
          _id={creator._id}
          isLoading={loadingThisCreator !== undefined}
          loadingThisCreator={loadingThisCreator ?? ""}
        />
        {attentionIcons}
        {warningIcon}
        {errorIcon}
      </div>
    );
  }

  // First stage pending — show "Activated" badge with trigger indicator
  if (isFirstStagePending) {
    const isDelay = currentStage?.trigger === "time_delay";
    return (
      <div className="flex items-center gap-1">
        <BadgeStatus status="activated" extraInfo={approvalDateLabel} />
        {isDelay ? (
          <HoverCard openDelay={100} closeDelay={150}>
            <HoverCardTrigger asChild>
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-help hover:bg-muted-foreground/10 data-[state=open]:bg-muted-foreground/10 rounded-sm py-0.5 px-1">
                <ClockIcon className="size-3" />
                <span>{delayCountdown || "sending soon"}</span>
              </span>
            </HoverCardTrigger>
            <HoverCardContent
              className="w-76 p-3 pt-2 rounded-lg"
              side="bottom"
              align="start"
            >
              <span className="font-medium text-sm">Creator Activated</span>
              <p className="text-muted-foreground mt-0.5 text-xs">
                <span className="font-medium text-foreground">
                  {currentStage?.name}
                </span>{" "}
                email will be sent {delayCountdown || "soon"}.
              </p>
              <div className="flex items-center gap-1.5 pt-2">
                <BadgeStatus
                  status="activated"
                  size="sm"
                  showDescription={false}
                />
                <ChevronRightIcon className="size-3 text-muted-foreground" />
                <BadgeStatus
                  status={status}
                  stages={stages}
                  size="sm"
                  showDescription={false}
                />
              </div>
            </HoverCardContent>
          </HoverCard>
        ) : (
          <HoverCard openDelay={100} closeDelay={150}>
            <HoverCardTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="size-7.5 shrink-0 text-foreground/70 hover:bg-muted-foreground/10 [&_svg]:size-3.5"
                disabled={isAdvancing || isSending}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdvance();
                }}
              >
                {isSending ? (
                  <Spinner />
                ) : (
                  <CircleChevronRightIcon />
                )}
              </Button>
            </HoverCardTrigger>
            <HoverCardContent
              className="w-76 p-3 pt-2 rounded-lg"
              side="bottom"
              align="start"
            >
              <span className="font-medium text-sm">Creator Activated</span>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Click to advance to{" "}
                <span className="font-medium text-foreground">
                  {currentStage?.name}
                </span>
                .
              </p>
              <div className="flex items-center gap-1.5 pt-2">
                <BadgeStatus
                  status="activated"
                  size="sm"
                  showDescription={false}
                />
                <ChevronRightIcon className="size-3 text-muted-foreground" />
                <BadgeStatus
                  status={status}
                  stages={stages}
                  size="sm"
                  showDescription={false}
                />
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        {warningIcon}
        {errorIcon}
      </div>
    );
  }

  // Custom status with manual advance button
  if (isManualPending) {
    return (
      <div className="flex items-center gap-2">
        <BadgeStatus
          status={status}
          stages={stages}
          extraInfo={approvalDateLabel}
        />
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="size-8 shrink-0 text-foreground/70 hover:bg-muted-foreground/10"
              disabled={isAdvancing || isSending}
              onClick={(e) => {
                e.stopPropagation();
                handleAdvance();
              }}
            >
              {isSending ? (
                <Spinner className="size-3" />
              ) : (
                <CircleChevronRightIcon className="size-3" />
              )}
            </Button>
          </HoverCardTrigger>
          <HoverCardContent className="w-76 p-3 pt-2 rounded-lg" side="bottom" align="start">
            <span className="font-medium text-sm">Manual Stage</span>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Click to complete{" "}
              <span className="font-medium text-foreground">
                {currentStage?.name}
              </span>
              {nextStage ? (
                <>
                  {" "}
                  and advance to{" "}
                  <span className="font-medium text-foreground">
                    {nextStage.name}
                  </span>
                </>
              ) : (
                " and mark all stages as completed"
              )}
              .
            </p>
            <div className="flex items-center gap-1.5 pt-2">
              <BadgeStatus
                status={status}
                stages={stages}
                size="sm"
                showDescription={false}
              />
              <ChevronRightIcon className="size-3 text-muted-foreground" />
              {nextStage ? (
                <BadgeStatus
                  status={nextStage.id}
                  stages={stages}
                  size="sm"
                  showDescription={false}
                />
              ) : (
                <BadgeStatus
                  status="completed"
                  size="sm"
                  showDescription={false}
                />
              )}
            </div>
          </HoverCardContent>
        </HoverCard>
        {warningIcon}
        {errorIcon}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <BadgeStatus
        status={status}
        stages={stages}
        pipeline={pipeline}
        extraInfo={approvalDateLabel}
      />
      {delayCountdown && (
        <span className="text-xs text-muted-foreground">{delayCountdown}</span>
      )}
      {isCustomStatus && stageInfo?.status === "waiting_reply" && (
        <span className="text-[10px] text-muted-foreground italic">
          Awaiting reply
        </span>
      )}
      {attentionIcons}
      {warningIcon}
      {errorIcon}
    </div>
  );
}

// Memoized: rows re-render for their own data, not for the table's UI state.
export default memo(StatusColumnCell);
